// frontend/src/modules/workspace/components/CheckboxRow.tsx
//
// A reusable labeled checkbox row matching the AdminRolesPage assignment
// editor look (coral-tinted when checked). Used for both role-selection and
// permission-selection lists.

import * as React from 'react';
import { cn } from '../../../lib/utils';

interface Props {
  checked: boolean;
  onToggle: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Tighter padding for dense permission lists. */
  compact?: boolean;
}

export function CheckboxRow({ checked, onToggle, title, subtitle, compact }: Props) {
  return (
    <label
      className={cn(
        'flex items-start gap-3 rounded-2xl cursor-pointer transition-colors',
        compact ? 'p-2.5' : 'p-3.5',
        checked
          ? 'bg-coral/10 ring-1 ring-coral/30'
          : 'bg-muted/40 ring-1 ring-border/40 hover:bg-muted/60',
      )}
    >
      <input type="checkbox" checked={checked} onChange={onToggle} className="sr-only" />
      <span
        className={cn(
          'mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-md ring-1 shrink-0',
          checked ? 'bg-coral text-white ring-coral' : 'bg-white dark:bg-white/10 ring-border',
        )}
      >
        {checked && (
          <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3">
            <path d="M2 6L5 9L10 3" />
          </svg>
        )}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[13.5px] font-semibold text-foreground">{title}</span>
        {subtitle && (
          <span className="block mt-0.5 text-[12px] text-muted-foreground leading-relaxed">{subtitle}</span>
        )}
      </span>
    </label>
  );
}
